
import { ProductSetInfoKeyTranslationsFragment } from '@/views/categoriesView/gql/__generated__/CategoriesView.query'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import {
  AddCustomProductSetInfoKeyDocument,
  AddCustomProductSetInfoKeyMutation,
  AddCustomProductSetInfoKeyMutationVariables
} from './gql/__generated__/addCustomProductSetInfoKey.mutation'
import { UpsertActionType } from '../categories/types'
import {
  UpdateProductSetInfoKeyDocument,
  UpdateProductSetInfoKeyMutation,
  UpdateProductSetInfoKeyMutationVariables
} from './gql/__generated__/updateProductSetInfoKey.mutation'
import ConfirmDialog from '../ConfirmDialog.vue'
import { CreatedInfoKey } from './types'
import QUERIES from '@/queries/queries'

@Component({
  components: {
    ConfirmDialog
  },
  mixins: [validationMixin]
})
export default class ProductSetInfoKeyModal extends Vue {
  @Prop({ required: true, default: null }) productSetInfoKeyToUpdate!: ProductSetInfoKeyTranslationsFragment | null
  @Prop({ required: true, default: UpsertActionType.SAVE }) actionType!: UpsertActionType
  @Prop({ default: () => [] }) knownProductSetInfoKeys!: ProductSetInfoKeyTranslationsFragment[]
  @Prop({ required: true, default: 0 }) usedInProductSetGroupsCount!: number

  showUpdateDialog = false

  labelNL = ''
  labelEN = ''

  isLoading = false

  get lang() {
    return this.$i18n.locale
  }

  get show() {
    return this.$store.state.manageCharacteristicsDialogs.showUpsertCharacteristicDialog
  }

  open() {
    this.$store.commit('setShowUpsertCharacteristicDialog', true)
  }

  close() {
    this.$store.commit('setShowUpsertCharacteristicDialog', false)
  }

  closeSelfAndOpenList() {
    this.close()
    this.$v.$reset()
    if (!this.productSetInfoKeyToUpdate) this.clear()
    this.$store.commit('setShowManageCharacteristicsDialog', true)
  }

  clear() {
    this.labelNL = ''
    this.labelEN = ''
  }

  get existingLabelsNL() {
    return this.knownProductSetInfoKeys
      .filter((x) => x.id !== this.productSetInfoKeyToUpdate?.id)
      .map((x) => x.infoLabelNL)
  }

  get existingLabelsEN() {
    return this.knownProductSetInfoKeys
      .filter((x) => x.id !== this.productSetInfoKeyToUpdate?.id)
      .map((x) => x.infoLabelEN)
  }

  validations() {
    return {
      labelNL: {
        required,
        isUnique: (value: string) => !this.existingLabelsNL.some((x) => x.toLowerCase() === value.toLowerCase())
      },
      labelEN: {
        required,
        isUnique: (value: string) => !this.existingLabelsEN.some((x) => x.toLowerCase() === value.toLowerCase())
      }
    }
  }

  get labelNLErrors() {
    const errors = new Array<string>()
    if (!this.$v.labelNL.$dirty) {
      return errors
    }
    if (!this.$v.labelNL.isUnique) {
      errors.push(this.$t('application.productSetKeys.add.errors.label.isUnique').toString())
    }
    if (!this.$v.labelNL.required) {
      errors.push(this.$t('application.productSetKeys.add.errors.label.required').toString())
    }
    return errors
  }

  get labelENErrors() {
    const errors = new Array<string>()
    if (!this.$v.labelEN.$dirty) {
      return errors
    }
    if (!this.$v.labelEN.isUnique) {
      errors.push(this.$t('application.productSetKeys.add.errors.label.isUnique').toString())
    }
    if (!this.$v.labelEN.required) {
      errors.push(this.$t('application.productSetKeys.add.errors.label.required').toString())
    }
    return errors
  }

  @Watch('productSetInfoKeyToUpdate')
  onProductSetInfoKeyToUpdate() {
    if (this.productSetInfoKeyToUpdate) {
      this.labelNL = this.productSetInfoKeyToUpdate.infoLabelNL
      this.labelEN = this.productSetInfoKeyToUpdate.infoLabelEN
    } else {
      this.labelNL = ''
      this.labelEN = ''
    }
  }

  submit() {
    this.$v.$touch()
    if (this.$v.labelNL.$pending || this.$v.labelNL.$error) return
    if (this.$v.labelEN.$pending || this.$v.labelEN.$error) return

    if (this.actionType === UpsertActionType.UPDATE && this.productSetInfoKeyToUpdate) {
      if (this.usedInProductSetGroupsCount === 0) {
        this.updateInfoKey()
      } else {
        this.showUpdateDialog = true
        this.close()
      }
    } else {
      this.createInfoKey()
    }
  }

  async createInfoKey() {
    try {
      this.isLoading = true
      const result = await this.$apolloMutate<
        AddCustomProductSetInfoKeyMutation,
        AddCustomProductSetInfoKeyMutationVariables
      >({
        mutation: AddCustomProductSetInfoKeyDocument,
        variables: { dutchLabel: this.labelNL, englishLabel: this.labelEN },
        error: 'E4204'
      })

      if (result !== false) {
        this.$emit('info-key-saved', {
          infoKeyId: result.data?.addCustomProductSetInfoKey,
          labelNL: this.labelNL,
          labelEN: this.labelEN
        } as CreatedInfoKey)

        this.closeSelfAndOpenList()
      }
    } finally {
      this.isLoading = false
    }
  }

  closeConfirmDialog() {
    this.showUpdateDialog = false
    this.open()
  }

  get updateConfirmationDescription() {
    return this.$t('application.productSetKeys.update.description')
      .toString()
      .replace('{{COUNT}}', this.usedInProductSetGroupsCount.toString())
  }

  async updateInfoKey() {
    this.showUpdateDialog = false
    this.open()

    try {
      this.isLoading = true
      const result = await this.$apolloMutate<
        UpdateProductSetInfoKeyMutation,
        UpdateProductSetInfoKeyMutationVariables
      >({
        mutation: UpdateProductSetInfoKeyDocument,
        variables: {
          productSetInfoKeyId: this.productSetInfoKeyToUpdate?.id,
          dutchLabel: this.labelNL,
          englishLabel: this.labelEN
        },
        // Refetch the CategoriesViewQuery to ensure the info key is updated in the list of the category modal and in the info key list modal
        refetchQueries: [QUERIES.CategoriesViewQuery],
        error: 'E4248'
      })

      if (result !== false) {
        this.closeSelfAndOpenList()
      }
    } finally {
      this.isLoading = false
    }
  }
}
